import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Layout,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  TablePaginationConfig,
} from 'antd';
import ButtonGroup from 'antd/es/button/button-group';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';
import { PagedResultState } from '../../common/CommonResult';
import queryString from 'query-string';
import { customsShipmentStatus } from './util';
import { notifyError, notifySuccess } from '../../store/utils';
import AddressZipcodeCascader from '../../lib/AddressZipcodeCascader';
import dayjs from 'dayjs';
import { CustomsProcessModal } from './CustomsProcessModal';

export const CustomsDetail = () => {
  const { airBill } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, changeModal] = useState<any>({ visible: false, item: null });
  const [scanLoading, setScanLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [fixAddress, changeFixAddress] = useState<any>({});
  const [searchState, changeSearchState] = useState<any>({});
  const [customsProcessModal, changeCustomsProcessModal] = useState<any>({
    visible: false,
    airBill: null,
  });

  useEffect(() => {
    if (location.search) {
      changeSearchState(queryString.parse(location.search));
      fetchShipments(airBill, queryString.parse(location.search));
    } else {
      setTimeout(() => {
        navigate(`/foreign/customs/${airBill}?page=1`);
      }, 100);
    }
  }, [location.search]);
  useEffect(() => {
    fetchData(airBill);
  }, [airBill]);
  const [customs, changeCustoms] = useState<any>({ loading: true, data: {} });
  const [shipments, changeShipments] = useState<PagedResultState<any>>({
    loading: true,
    result: { data: [], total: 0, page: 1 },
  });

  const fetchData = async (airBill: any) => {
    try {
      changeCustoms((prev: any) => ({ ...prev, loading: true }));
      const response = await API.get({ apiVersion: 'api' })('/customs/detail?airBill=' + airBill);
      changeCustoms({ loading: false, data: response.result.data });
    } catch (e: any) {
      notifyError(e.message || 'Гаалийн мэдүүлэг ачаалахад алдаа гарлаа');
      changeCustoms((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const syncParcel = async (trackingNumber: string, zipcode: string) => {
    try {
      setSyncLoading(true);
      await API.post({ apiVersion: 'api' })('/foreign/shipping/sync', {
        trackingNumber: trackingNumber,
        zipcode: zipcode,
      });
      notifySuccess('Хаяг амжилттай шинэчлэгдлээ');
      changeFixAddress({});
      fetchShipments(airBill, searchState);
    } catch (e: any) {
      notifyError(e.message || 'Хаяг шинэчлэхэд алдаа гарлаа');
    } finally {
      setSyncLoading(false);
    }
  };
  const fetchShipments = async (airBill: any, filter: any) => {
    try {
      changeShipments((prev) => ({ ...prev, loading: true }));
      const response = await API.get({ apiVersion: 'api' })(
        `/customs/shipments/${airBill}?${queryString.stringify(filter)}`
      );
      changeShipments({ loading: false, result: response.result });
    } catch (e: any) {
      notifyError(e.message || 'Илгээмжийн жагсаалт ачаалахад алдаа гарлаа');
      changeShipments((prev) => ({ ...prev, loading: false }));
    }
  };
  const onCustomsStatus = useCallback(
    async (scanDate: string, code: string) => {
      try {
        setScanLoading(true);
        const response = await API.post({ apiVersion: 'api' })('/customs/scan', {
          airBill: airBill,
          status: code,
          scanDate: scanDate,
        });
        changeCustoms({ loading: false, data: response.result });
        notifySuccess('Гаалийн төлөв амжилттай шинэчлэгдлээ');
        return true;
      } catch (e: any) {
        notifyError(e.message || 'Алдаа гарлаа');
        return false;
      } finally {
        setScanLoading(false);
      }
    },
    [airBill]
  );
  const columns = [
    {
      title: 'Код',
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
    },
    {
      title: 'Илгээгч',
      key: 'sender',
      render: (data: any) => {
        return (
          <span>
            {data.senderFirstName}-{data.senderLastName} {data.senderPhone}
          </span>
        );
      },
    },
    {
      title: 'Хүлээн авагч',
      key: 'receiver',
      render: (data: any) => {
        return (
          <span>
            {data.receiverProvince}-{data.receiverDistrict}-{data.receiverAddress1}
            <br />
            {data.receiverLastName}-{data.receiverFirstName} {data.receiverPhone}
          </span>
        );
      },
    },
    {
      title: 'Төлөв',
      key: 'status',
      render: (data: any) => {
        return (
          <span>
            {data.status?.name || 'Үүссэн'} - {data.reason?.name ?? ''}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (data: any) => {
        return (
          <span>
            <Popconfirm
              title="Хаяг шинэчлэх"
              open={data.no === fixAddress?.order?.no}
              onConfirm={() => syncParcel(data.trackingNumber, fixAddress.zipcode)}
              onCancel={() => changeFixAddress({})}
              okButtonProps={{ loading: syncLoading }}
              cancelButtonProps={{ disabled: syncLoading }}
              description={`Та энэ илгээмжийн зиф кодыг ${fixAddress.zipcode} кодоор солихдоо итгэлтэй байна уу`}
            >
              <span />
            </Popconfirm>
            {data.orderNo ? (
              data.orderNo
            ) : (
              <AddressZipcodeCascader
                onChange={(e: any) => {
                  if (e.length === 3) {
                    changeFixAddress({ zipcode: e[2], order: data });
                  }
                }}
              />
            )}
          </span>
        );
      },
    },
  ];
  const handleTableChange = (pagination: TablePaginationConfig) => {
    const params = queryString.parse(location.search);
    const search = {
      ...params,
      page: pagination.current || 1,
      size: pagination.pageSize,
    };
    navigate(`/foreign/customs/${airBill}?${queryString.stringify(search)}`);
  };
  const handleStatusChange = (status?: string) => {
    const params = queryString.parse(location.search);
    const search = {
      ...params,
      status,
    };
    navigate(`/foreign/customs/${airBill}?${queryString.stringify(search)}`);
  };
  const nextStatusList = useMemo(() => {
    if (customs.data && customs.data.shipmentStatus) {
      let current = customsShipmentStatus[customs.data.shipmentStatus.cd];
      return current.next.map((item: string) => {
        return {
          code: item,
          name: customsShipmentStatus[item].name,
        };
      });
    } else {
      return [];
    }
  }, [customs.data]);
  return (
    <Layout.Content style={{ margin: '24px 16px 0' }}>
      <h3>Гаалийн мэдүүлэг-{airBill}</h3>
      <Row gutter={16}>
        <Col span={8}>
          <Statistic
            loading={customs.loading}
            title="Төлөв"
            value={customs.data.shipmentStatus?.name}
          />
        </Col>
        <Col span={8}>
          <span onClick={() => handleStatusChange()}>
            <Statistic
              loading={customs.loading}
              title="Илгээмж"
              value={customs.data?.shipmentCount}
            />
          </span>
        </Col>
        <Col span={8}>
          {customs.data?.pendingCount > 0 ? (
            <Statistic
              loading={customs.loading}
              title={
                <Button onClick={() => handleStatusChange('CUSTOMS_FAILED')}>Хураагдсан</Button>
              }
              value={customs.data.pendingCount}
            />
          ) : (
            <Statistic
              loading={customs.loading}
              title="Хураагдсан"
              value={customs.data.pendingCount}
            />
          )}
        </Col>
        <Col span={8}>
          <Statistic
            loading={customs.loading}
            title="Харилцагч"
            value={customs.data.customer?.name}
          />
        </Col>
      </Row>
      <Divider />
      <div className="search-area">
        <Space wrap style={{ marginBottom: 15 }}>
          <ButtonGroup>
            {nextStatusList.map((item: any) => (
              <Button
                loading={scanLoading}
                key={item.code}
                type="primary"
                onClick={() => changeModal({ visible: true, item: item })}
              >
                {item.name}
              </Button>
            ))}
          </ButtonGroup>
        </Space>
        <Divider />
        <Space>
          <Button
            type="primary"
            onClick={() => changeCustomsProcessModal({ visible: true, airBill: airBill })}
          >
            Хураагдсан илгээмж бүртгэх
          </Button>
        </Space>
      </div>
      <Card
        extra={
          <Space>
            {searchState.status && (
              <Button
                target="_blank"
                href={`/api/customs/detail/cn23/${airBill}?status=${searchState.status}`}
              >
                Саатсан ачааны мэдүүлэг
              </Button>
            )}
            <Button
              target="_blank"
              href={
                searchState.status
                  ? `/api/customs/detail/excel/${airBill}?status=${searchState.status}`
                  : `/api/customs/detail/excel/${airBill}`
              }
              type="primary"
            >
              Excel татах
            </Button>
          </Space>
        }
        title="Илгээмжүүд"
      >
        <Table
          columns={columns}
          dataSource={shipments.result.data}
          loading={shipments.loading}
          pagination={{
            current: shipments?.result?.page || 1,
            size: 'small',
            pageSize: shipments?.result?.size || 10,
            total: shipments?.result?.total,
          }}
          onChange={handleTableChange}
        />
      </Card>
      <CustomsProcessModal
        visible={customsProcessModal.visible}
        onCancel={() => changeCustomsProcessModal({ visible: false })}
        onOk={(scanDate: any, code: string) => {
          fetchData(airBill);
          changeCustomsProcessModal({ visible: false });
        }}
        airBill={customsProcessModal.airBill}
      />
      <CustomsStatusModal
        visible={modal.visible}
        confirmLoading={scanLoading}
        onCancel={() => changeModal({ visible: false })}
        onOk={async (scanDate: string, code: string) => {
          const success = await onCustomsStatus(scanDate, code);
          if (success) {
            changeModal({ visible: false });
          }
        }}
        item={modal.item}
      />
    </Layout.Content>
  );
};

interface CustomsStatusModalProps {
  visible: boolean;
  item: any;
  confirmLoading?: boolean;
  onCancel: () => void;
  onOk: (scanDate: any, code: string) => void;
}
const CustomsStatusModal = ({
  visible,
  onCancel,
  onOk,
  item,
  confirmLoading,
}: CustomsStatusModalProps) => {
  const [date, setDate] = useState<any>(null);
  useEffect(() => {
    if (visible) {
      setDate(dayjs());
    }
  }, [visible]);
  const onSubmit = useCallback(() => {
    onOk(dayjs(date), item.code);
  }, [date, item]);
  return (
    <Modal
      title={`Гаалийн төлөв: ${item?.name}`}
      open={visible}
      confirmLoading={confirmLoading}
      onOk={onSubmit}
      onCancel={() => onCancel()}
      cancelButtonProps={{ disabled: confirmLoading }}
    >
      <DatePicker showTime onChange={(e) => setDate(e)} value={date} />
    </Modal>
  );
};
